import React, { useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { selectors } from "reducers";

import { useOutsideClick } from "hooks";
import MdArrowDropUp from "react-icons/lib/md/arrow-drop-up";
import MdArrowDropDown from "react-icons/lib/md/arrow-drop-down";
import MdTrash from "react-icons/lib/md/delete-sweep";
import SelectedIcon from "react-icons/lib/io/ios-checkmark-outline";
import MdRemove from "react-icons/lib/md/close";
import unique from "lodash/uniq";
import groupBy from "lodash/groupBy";

const MultiSelectDropdown = ({
  options,
  values,
  searchable,
  onChange,
  onClear,
  placeHolder,
  renderValues,
  shiftSelect,
  onRemove,
  onBlur,
  dropdownStyle
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [headerTooltip, setHeaderTooltip] = useState("");
  const [filterText, setFilterText] = useState("");
  const [serviceLineFilters, setServiceLineFilters] = useState(() => new Set());
  const [showInactive, setShowInactive] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState(() => new Set());
  const userRole = useSelector(selectors.getUserRole);
  React.useEffect(() => {
    setHeaderTooltip(`${values.length} Selected`);
  }, [values]);
  function clearSelected(e) {
    e.stopPropagation();
    if (onClear) onClear();
    onChange([]);
  }
  function filterByText(item) {
    if (filterText.length > 0 && typeof item.text === "string") {
      return item.text.toLowerCase().includes(filterText.toLowerCase());
    }
    return true;
  }
  function toggleSelected(value) {
    if (!values.includes(value)) {
      onChange([...values, value]);
    } else {
      onChange(values.filter(v => v !== value));
    }
  }
  function handleSelectAll() {
    const newvalues = filteredOptions.map(e => e.value);
    onChange(unique([...values, ...newvalues]));
  }

  function toggleServiceLineFilter(line) {
    setServiceLineFilters(prev => {
      const next = new Set(prev);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });
  }

  function toggleTeamExpand(teamName) {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamName)) next.delete(teamName);
      else next.add(teamName);
      return next;
    });
  }

  function toggleTeamSelection(teamRows, allSelected) {
    const teamValues = teamRows.map(r => r.value);
    if (allSelected) {
      onChange(values.filter(v => !teamValues.includes(v)));
    } else {
      onChange(unique([...values, ...teamValues]));
    }
  }

  // Memoized so it isn't recomputed for every filterOptions() call site on
  // every render (header InputTitle, stats bar, and the row IIFE all read it).
  // filterText isn't a dep — text-search runs AFTER this list is computed.
  const filteredOptions = useMemo(() => {
    let fc = options;
    if (serviceLineFilters.size > 0) {
      // Match ANY selected line (union) — a company shows up if it touches
      // at least one of the checked service lines.
      fc = fc.filter(v =>
        (serviceLineFilters.has("SaaS") && v.is_saas) ||
        (serviceLineFilters.has("Resupply") && (v.does_cpap_resupply || v.does_cgm_resupply)) ||
        (serviceLineFilters.has("Compliance") && v.tracks_compliance) ||
        (serviceLineFilters.has("Onboarding") && v.does_onboarding)
      );
    }
    if (!showInactive) {
      // preserve already-assigned inactive companies so admins can uncheck them
      fc = fc.filter(v => v.active || values.includes(v.value));
    }
    return fc;
  }, [options, serviceLineFilters, showInactive, values]);

  const dropDownRef = useRef();
  useOutsideClick(dropDownRef, () => {
    if (isOpen) {
      setIsOpen(false);
      // Clear the search text so reopening the picker doesn't silently
      // reapply the last query.
      setFilterText("");
      if (typeof onBlur === "function") onBlur();
    }
  });
  return (
    <div>
    <div className="multi-select-dropDown-container" ref={dropDownRef}>
      <div
        className="select-dropDown-header"
        onClick={() => {
          if (!isOpen) setIsOpen(true);
        }}
        >
        {isOpen && searchable ? (
          <input
          className="select-dropDown-header-title"
          type="search"
          onClick={e => e.stopPropagation()}
          value={filterText}
            onChange={({ target }) => setFilterText(target.value)}
            />
        ) : (
          <React.Fragment>
            <div className="select-dropDown-header-title" title={headerTooltip}>
              {renderValues ? (
                <InputTitle
                  selected={filteredOptions.filter(({ value }) =>
                    values.includes(value)
                  )}
                  toggleSelected={toggleSelected}
                  placeHolder={placeHolder}
                  onRemove={onRemove}
                  />
                  ) : (
                    headerTooltip
                    )}
            </div>
          </React.Fragment>
        )}
        {
          values.length > 0 ? (
            <MdTrash
            color="red"
              style={{ marginRight: "10px", cursor: "pointer" }}
              onClick={clearSelected}
              />
          ) : isOpen ? 
            (<MdArrowDropUp />) : 
            (< MdArrowDropDown />)
        }
      </div>
      {isOpen &&
      (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '45% 25% 20% 10%', alignItems: 'center' }}>
            <div>
              <input type="checkbox" id="filterSaaS" checked={serviceLineFilters.has("SaaS")} onChange={() => toggleServiceLineFilter("SaaS")} />
              <label htmlFor="filterSaaS">SaaS</label>
              <input type="checkbox" id="filterResupply" checked={serviceLineFilters.has("Resupply")} onChange={() => toggleServiceLineFilter("Resupply")} />
              <label htmlFor="filterResupply">Resupply</label>
              <input type="checkbox" id="filterCompliance" checked={serviceLineFilters.has("Compliance")} onChange={() => toggleServiceLineFilter("Compliance")} />
              <label htmlFor="filterCompliance">Compliance</label>
              <input type="checkbox" id="filterOnboarding" checked={serviceLineFilters.has("Onboarding")} onChange={() => toggleServiceLineFilter("Onboarding")} />
              <label htmlFor="filterOnboarding">Onboarding</label>
            </div>
            <div>
              <input type="checkbox" id="showInactive" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              <label htmlFor="showInactive">Show inactive companies</label>
            </div>
            <div>
              <b>Total Selected:</b> {values.length} / <b>Filtered:</b> {filteredOptions.length} / <b>Total:</b> {options.length}
            </div>
            <div>
              {userRole === "Administrator" && (
                <button type="button" title="This will add ALL companies in the current filter view.  It does not remove companies." onClick={handleSelectAll}>
                  Add All
                </button>
              )}
            </div>
          </div>
        <ul className="select-dropDown-list" style={dropdownStyle} onClick={e => e.stopPropagation()}>
          {(() => {
            const visible = filteredOptions.filter(filterByText);
            const assignedInactive = visible.filter(o => !o.active && values.includes(o.value));
            const assignedInactiveIds = new Set(assignedInactive.map(o => o.value));
            const teamVisible = visible.filter(o => !assignedInactiveIds.has(o.value));
            const sortByText = (a, b) => (a.text || "").localeCompare(b.text || "");
            const searchActive = filterText.length > 0;
            const flatMode = serviceLineFilters.size > 0;
            const INACTIVE_SECTION = "Inactive Assignments";

            const renderRow = (row, shiftBoundary, isInactiveSection, renderedRows) => {
              const { value, text, active, assignedTeam, is_saas, tracks_compliance, does_cpap_resupply, does_cgm_resupply, does_onboarding } = row;
              const isSelected = values.includes(value);
              return (
                <li
                  className={`select-dropDown-list-item ${isSelected ? "active" : ""}`}
                  key={value}
                  onClick={e => {
                    if (shiftSelect && e.shiftKey) {
                      onChange([
                        ...renderedRows
                          .slice(0, shiftBoundary + 1)
                          .map(o => o.value)
                      ]);
                    } else toggleSelected(value);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '18px', flex: '0 0 auto' }}>
                      {isSelected && <SelectedIcon style={{ float: "left" }} />}
                    </div>
                    <div className="select-dropDown-row-name" style={{ flex: '1 1 auto', minWidth: 0 }}>
                      {text}
                      {!active && (
                        <span className="select-dropDown-inactive-badge">
                          (inactive)
                        </span>
                      )}
                      {isInactiveSection && (
                        <span className="select-dropDown-inactive-team-suffix">
                          {" — "}{assignedTeam || "Unassigned"}
                        </span>
                      )}
                    </div>
                    <div className="select-dropDown-service-badges">
                      {is_saas && <span className="select-dropDown-service-badge saas">SaaS</span>}
                      {tracks_compliance && <span className="select-dropDown-service-badge compliance">Compliance</span>}
                      {does_cpap_resupply && <span className="select-dropDown-service-badge cpap">CPAP</span>}
                      {does_cgm_resupply && <span className="select-dropDown-service-badge cgm">CGM</span>}
                      {does_onboarding && <span className="select-dropDown-service-badge onboarding">Onboarding</span>}
                    </div>
                  </div>
                </li>
              );
            };

            const renderInactiveSectionHeader = (rows, expanded, onHeaderClick) => (
              <li
                className="select-dropDown-team-header inactive-section"
                onClick={onHeaderClick}
              >
                <input
                  type="checkbox"
                  className="select-dropDown-team-select"
                  aria-label={`Deselect all ${rows.length} inactive assignments`}
                  checked
                  ref={el => { if (el) el.indeterminate = false; }}
                  data-indeterminate="false"
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleTeamSelection(rows, true)}
                />
                <span className="select-dropDown-team-header-name">{INACTIVE_SECTION}</span>
                <span className="select-dropDown-team-header-count">({rows.length})</span>
                <span className="select-dropDown-team-header-chevron">{expanded ? "▾" : "▸"}</span>
              </li>
            );

            // Flat mode: any service-line filter (SaaS / Resupply / Compliance)
            // renders an alphabetical list with no team accordion. Inactive
            // Assignments section still pinned at the bottom when applicable.
            if (flatMode) {
              const flatVisible = [...teamVisible].sort(sortByText);
              const sortedInactive = [...assignedInactive].sort(sortByText);
              const inactiveExpanded = expandedTeams.has(INACTIVE_SECTION) || searchActive;
              const renderedRows = [
                ...flatVisible,
                ...(inactiveExpanded ? sortedInactive : [])
              ];
              return (
                <React.Fragment>
                  {flatVisible.map((row, i) => renderRow(row, i, false, renderedRows))}
                  {sortedInactive.length > 0 && (
                    <React.Fragment>
                      {renderInactiveSectionHeader(sortedInactive, inactiveExpanded, () => toggleTeamExpand(INACTIVE_SECTION))}
                      {inactiveExpanded && sortedInactive.map((row, i) =>
                        renderRow(row, flatVisible.length + i, true, renderedRows)
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              );
            }

            // Team accordion mode ("All" filter). Teams collapsed by default,
            // Inactive Assignments section pinned last.
            const groups = groupBy(teamVisible, o => o.assignedTeam || "Unassigned");
            const teamNames = Object.keys(groups)
              // Fully-inactive teams stay hidden by default, but surface when
              // Show Inactive is on so admins can still manage assignments
              // for retired companies.
              .filter(t => showInactive || groups[t].some(r => r.active))
              .sort((a, b) => {
                if (a === "Unassigned") return 1;
                if (b === "Unassigned") return -1;
                return a.localeCompare(b);
              });
            const sortedByTeam = teamNames.reduce((acc, t) => {
              acc[t] = [...groups[t]].sort(sortByText);
              return acc;
            }, {});
            const orderedGroups = [...teamNames];
            if (assignedInactive.length > 0) {
              sortedByTeam[INACTIVE_SECTION] = [...assignedInactive].sort(sortByText);
              orderedGroups.push(INACTIVE_SECTION);
            }
            const isTeamExpanded = t => expandedTeams.has(t) || searchActive;
            const renderedRows = orderedGroups.flatMap(t => isTeamExpanded(t) ? sortedByTeam[t] : []);
            let flatIndex = -1;
            return orderedGroups.map(teamName => {
              const isInactiveSection = teamName === INACTIVE_SECTION;
              const teamRows = sortedByTeam[teamName];
              const totalInTeam = teamRows.length;
              const selectedInTeam = teamRows.filter(r => values.includes(r.value)).length;
              const allSelected = totalInTeam > 0 && selectedInTeam === totalInTeam;
              const partialSelected = selectedInTeam > 0 && selectedInTeam < totalInTeam;
              const expanded = isTeamExpanded(teamName);
              const headerClass = [
                "select-dropDown-team-header",
                isInactiveSection ? "inactive-section" : "",
                !isInactiveSection && allSelected ? "all-selected" : "",
                !isInactiveSection && partialSelected ? "partial-selected" : ""
              ].filter(Boolean).join(" ");
              return (
                <React.Fragment key={teamName}>
                  <li
                    className={headerClass}
                    onClick={() => toggleTeamExpand(teamName)}
                  >
                    <input
                      type="checkbox"
                      className="select-dropDown-team-select"
                      aria-label={
                        isInactiveSection
                          ? `Deselect all ${totalInTeam} inactive assignments`
                          : `Select all ${totalInTeam} companies in ${teamName}`
                      }
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = partialSelected; }}
                      data-indeterminate={partialSelected ? "true" : "false"}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleTeamSelection(teamRows, allSelected)}
                    />
                    <span className="select-dropDown-team-header-name">{teamName}</span>
                    <span className="select-dropDown-team-header-count">
                      {isInactiveSection
                        ? `(${totalInTeam})`
                        : `(${selectedInTeam} of ${totalInTeam})`}
                    </span>
                    <span className="select-dropDown-team-header-chevron">
                      {expanded ? "▾" : "▸"}
                    </span>
                  </li>
                  {expanded && teamRows.map(row => {
                    flatIndex += 1;
                    return renderRow(row, flatIndex, isInactiveSection, renderedRows);
                  })}
                </React.Fragment>
              );
            });
          })()}
        </ul>
        </div>
)}
    </div>
</div>
  );
};

const InputTitle = ({ selected, toggleSelected, placeHolder, onRemove }) => {
  if (selected.length <= 0 && placeHolder) return placeHolder;
  else
    return selected.map((s) => (
      <span
        key={s.value}
        style={{
          display: "inline-flex",
          alignItems: "left",
          flexWrap: "wrap",
          width:'30%'
        }}
      >
        <i
          onClick={e => {
            e.stopPropagation();
            toggleSelected(s.value);
            if (typeof onRemove === "function") onRemove(s);
          }}
          className="flat-icon-button"
          style={{ fontSize: ".75rem", padding: "0 2px" }}
        >
          <MdRemove color="red" />
        </i>
        {s.text}
      </span>
    ));
};

export default MultiSelectDropdown;

MultiSelectDropdown.defaultProps = {
  placeHolder: "",
  values: [],
  searchable: true,
  renderValues: true,
  dropdownStyle: {}
};

MultiSelectDropdown.propTypes = {
  shiftSelect: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  searchable: PropTypes.bool,
  placeHolder: PropTypes.string,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.any
    })
  ).isRequired,
  onBlur: PropTypes.func,
  onClear: PropTypes.func,
  renderValues: PropTypes.bool,
  onRemove: PropTypes.func,
  dropdownStyle: PropTypes.object
};
